import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Users, Ticket, BellRing, Loader2, Check, CalendarDays } from "lucide-react";
import { API } from "@/lib/gp";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PLATFORM_META } from "@/seo/launchPages";

export default function Tournaments() {
  usePageMeta(PLATFORM_META["/tournaments"]);
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", discord: "", handle: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyState, setNotifyState] = useState("idle"); // idle | busy | sent

  const load = () => fetch(`${API}/next-event`).then((r) => r.json()).then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const set = (k) => (e) => { const v = e.target.value; setForm((f) => ({ ...f, [k]: v })); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/event-signup`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      const d = await res.json();
      if (!res.ok) throw new Error();
      setDone(true);
      trackEvent("generate_lead", { lead_source: "tournament_waitlist" });
      toast.success(d.message || "You're on the waitlist!");
      load();
    } catch { toast.error("Couldn't join the waitlist — check your email and try again."); }
    finally { setBusy(false); }
  };

  // While registration is closed, "Join Waitlist" signs people up for the confirmed
  // (double opt-in) update list so they hear when registration opens.
  const notify = async (e) => {
    e.preventDefault();
    const email = (notifyEmail || user?.email || "").trim();
    if (!email) { toast.error("Enter your email."); return; }
    setNotifyState("busy");
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "");
      setNotifyState("sent");
      trackEvent("generate_lead", { lead_source: "tournament_waitlist_notify" });
      toast.success(d.message || "Check your email to confirm.");
    } catch {
      setNotifyState("idle");
      toast.error("Couldn't add you right now. Check your email address and try again.");
    }
  };

  const ev = data?.event;
  const registrationEnabled = data?.registration_enabled === true;

  return (
    <div className="mx-auto max-w-5xl px-5 md:px-8 py-12 md:py-16">
      {/* Status banner */}
      <div className="glass rounded-2xl p-4 flex items-center gap-3 mb-8" data-testid="tournament-status">
        <BellRing className="text-gp-gold shrink-0" size={20} />
        <p className="text-sm text-white/75">
          {registrationEnabled ? (
            <>
              Next event: <span className="text-white font-semibold">December 12, 2026</span>.
              Join the waitlist below to hold a seat. Waitlist size stays private.
            </>
          ) : (
            <>
              No tournament is running <span className="text-white font-semibold">right now</span>. Registration will open
              <span className="text-gp-blue font-semibold"> after launch readiness review</span>.
            </>
          )}
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 items-start">
        {/* Next event */}
        <div>
          <span className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">Upcoming Tournament</span>
          <h1 className="font-unbounded font-black tracking-tighter text-3xl sm:text-4xl uppercase mt-2 leading-tight">
            {ev ? ev.name : "GamePlat Balatro Community Challenge"}
          </h1>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="flex items-center gap-1.5 text-sm bg-white/5 rounded-full px-3 py-1.5"><CalendarDays size={14} className="text-gp-gold" /> {ev?.date_label || "December 12, 2026"}</span>
            <span className="flex items-center gap-1.5 text-sm bg-white/5 rounded-full px-3 py-1.5"><Users size={14} className="text-gp-blue" /> Up to {ev?.players || 64} seats</span>
            <span className="flex items-center gap-1.5 text-sm bg-white/5 rounded-full px-3 py-1.5"><Ticket size={14} className="text-gp-green" /> {ev?.entry || "Free Entry"}</span>
            <span className="flex items-center gap-1.5 text-sm bg-white/5 rounded-full px-3 py-1.5"><Trophy size={14} className="text-gp-gold" /> {ev?.type || "Community Competition"}</span>
          </div>
          <p className="text-white/70 mt-5 leading-relaxed">{ev?.blurb}</p>
          <p className="text-white/50 mt-3 text-sm">{ev?.date_note}</p>
          {registrationEnabled && <p className="mt-4 text-sm text-gp-blue font-semibold" data-testid="signup-count">{data.registered} players already registered</p>}
        </div>

        {/* Registration */}
        <div className="glass rounded-3xl p-7">
          {!registrationEnabled ? (
            <div className="text-center py-10" data-testid="signup-closed">
              <span className="grid place-items-center h-16 w-16 rounded-2xl bg-gp-gold/15 text-gp-gold mx-auto mb-5"><BellRing size={30} /></span>
              <h3 className="font-unbounded font-bold text-2xl">Registration coming soon</h3>
              <p className="text-white/60 mt-2">Join the waitlist and we'll email you the moment registration opens.</p>
              {notifyState === "sent" ? (
                <p className="mt-6 text-gp-green font-semibold" data-testid="notify-sent">
                  <Check size={16} className="inline mr-1" /> Almost done: confirm the email we just sent you.
                </p>
              ) : (
                <form onSubmit={notify} className="mt-6 flex flex-col sm:flex-row gap-3 text-left" data-testid="notify-form">
                  <Input data-testid="notify-email" type="email" value={notifyEmail || user?.email || ""} onChange={(e) => setNotifyEmail(e.target.value)} placeholder="Email" className="bg-black/30 border-white/10 h-12 rounded-xl" />
                  <Button type="submit" data-testid="notify-submit" disabled={notifyState === "busy"} className="rounded-full h-12 px-6 bg-gp-blue hover:bg-gp-blue/90 font-bold shrink-0">
                    {notifyState === "busy" ? <Loader2 className="animate-spin" size={18} /> : "Join Waitlist"}
                  </Button>
                </form>
              )}
              <p className="mt-3 text-xs text-white/40">We only email you after you confirm. See our <a href="/privacy" className="text-gp-blue hover:underline">Privacy Policy</a>.</p>
            </div>
          ) : done ? (
            <div className="text-center py-10" data-testid="signup-success">
              <span className="grid place-items-center h-16 w-16 rounded-2xl bg-gp-green/15 text-gp-green mx-auto mb-5"><Check size={30} /></span>
              <h3 className="font-unbounded font-bold text-2xl">You're on the waitlist!</h3>
              <p className="text-white/60 mt-2">You're in for December 12, 2026. We'll email registration details. Waitlist size stays private.</p>
            </div>
          ) : (
            <>
              <h3 className="font-unbounded font-bold text-xl">Join the tournament waitlist</h3>
              <p className="text-white/55 text-sm mt-1">Hold a seat for December 12, 2026. We email waitlist players — we do not publish a headcount.</p>
              <form onSubmit={submit} className="mt-5 space-y-3" data-testid="event-signup-form">
                <Input data-testid="signup-name" value={form.name} onChange={set("name")} placeholder="Your name / gamertag" className="bg-black/30 border-white/10 h-12 rounded-xl" />
                <Input data-testid="signup-email" type="email" value={form.email} onChange={set("email")} placeholder="Email" className="bg-black/30 border-white/10 h-12 rounded-xl" />
                <Input data-testid="signup-discord" value={form.discord} onChange={set("discord")} placeholder="Discord handle (optional)" className="bg-black/30 border-white/10 h-12 rounded-xl" />
                <Input data-testid="signup-handle" value={form.handle} onChange={set("handle")} placeholder="Balatro/in-game name (optional)" className="bg-black/30 border-white/10 h-12 rounded-xl" />
                <Button type="submit" data-testid="signup-submit" disabled={busy} className="w-full rounded-full py-6 bg-gp-blue hover:bg-gp-blue/90 font-bold text-base shadow-[0_0_22px_rgba(25,195,125,0.4)]">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : "Join Waitlist"}
                </Button>
                <p className="text-xs leading-relaxed text-white/40">
                  By joining, you agree to receive tournament and GamePlat updates. You can opt out anytime through <a href="/support" className="text-gp-blue hover:underline">Support</a>. See our <a href="/privacy" className="text-gp-blue hover:underline">Privacy Policy</a>.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
